import { useAtomValue } from 'jotai';
import { useState } from 'react';

import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	Box,
	SkeletonRect,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import { useGetInventoryItem } from '../../../api/useGetInventoryItem';
import { drawerAtom } from '../../../atoms/editDrawerAtoms';

import './MediaDetails.scss';

interface ImageGridProps {
	imageUrls: string[];
	make?: string;
	model?: string;
}

const ImageGrid = ({ imageUrls, make, model }: ImageGridProps) => {
	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
				gap: '12px',
			}}>
			{imageUrls.map((url, index) => (
				<div
					key={url}
					style={{
						aspectRatio: '3/2',
						borderRadius: '8px',
						overflow: 'hidden',
						backgroundColor: '#f5f5f5',
						border: '1px solid #e0e0e0',
					}}>
					<img
						src={url}
						alt={`${make} ${model} - Image ${index + 1}`}
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
						}}
					/>
				</div>
			))}
		</div>
	);
};

const LoadingState = () => {
	return (
		<Box padding={{ y: 20 }}>
			<Stack itemsSpacing='12'>
				<Text size='body' weight='semibold'>
					Media
				</Text>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
						gap: '12px',
					}}>
					{Array.from({ length: 4 }, (_, index) => (
						<div
							key={index}
							style={{
								aspectRatio: '3/2',
								borderRadius: '8px',
								overflow: 'hidden',
								backgroundColor: '#f5f5f5',
								border: '1px solid #e0e0e0',
							}}>
							<SkeletonRect width='100%' height='100%' />
						</div>
					))}
				</div>
			</Stack>
		</Box>
	);
};

const EmptyState = () => {
	return (
		<Box padding={{ y: 20 }}>
			<Stack itemsSpacing='12'>
				<Text size='body' weight='semibold'>
					Media (0)
				</Text>
				<Box rounding='8' background='secondary' padding='16'>
					<div
						style={{
							minHeight: '120px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}>
						<Text size='bodySmall' isItalic color='secondary' align='center'>
							Upload at least one image or video
						</Text>
					</div>
				</Box>
			</Stack>
		</Box>
	);
};

type MediaContentProps = {
	imageUrls: string[];
	imageCount: number;
	make?: string;
	model?: string;
};

const MediaContent = ({ imageUrls, imageCount, make, model }: MediaContentProps) => {
	const [isExpanded, setIsExpanded] = useState(true);
	const hasMoreThanFourImages = imageCount > 4;
	const displayedImages = isExpanded ? imageUrls : imageUrls.slice(0, 4);

	// When 4 or fewer images, render without accordion (no chevron, always show all)
	if (!hasMoreThanFourImages) {
		return (
			<Box padding={{ y: 20 }}>
				<Stack itemsSpacing='12'>
					<Text size='body' weight='semibold'>
						{`Media (${imageCount})`}
					</Text>
					<ImageGrid imageUrls={imageUrls} make={make} model={model} />
				</Stack>
			</Box>
		);
	}

	// When more than 4 images, use accordion with collapsible functionality
	return (
		<Accordion isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
			<AccordionHeader>
				<Box padding={{ y: 20, x: 0 }} className='media-details__accordion-header-content'>
					<Text size='body' weight='semibold' align='left'>
						{`Media (${imageCount})`}
					</Text>
				</Box>
			</AccordionHeader>
			{!isExpanded && (
				<Box padding={{ top: 0, bottom: 12 }}>
					<ImageGrid imageUrls={displayedImages} make={make} model={model} />
				</Box>
			)}
			<AccordionBody>
				<Box padding={{ top: 12, bottom: 12 }}>
					<Stack itemsSpacing='16'>
						<ImageGrid imageUrls={imageUrls} make={make} model={model} />
					</Stack>
				</Box>
			</AccordionBody>
		</Accordion>
	);
};

export const MediaDetails = () => {
	const { selectedItemId } = useAtomValue(drawerAtom);
	const { data: item, isLoading } = useGetInventoryItem(selectedItemId ?? '');

	// Show loading if actively loading OR if we have an ID but no data yet
	if (isLoading || Boolean(selectedItemId && !item)) {
		return <LoadingState />;
	}

	const imageUrls = item?.imageUrls ?? [];
	const imageCount = imageUrls.length;

	return imageCount === 0 ? (
		<EmptyState />
	) : (
		<MediaContent
			imageUrls={imageUrls}
			imageCount={imageCount}
			make={item?.make}
			model={item?.model}
		/>
	);
};
